import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  message,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  BankOutlined,
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import MainLayout from '../../layouts/MainLayout';
import { companiesApi, packagesApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import PersianDatePicker from '../../components/PersianDatePicker';
import { jalaliValueToApi } from '../../utils/jalaliApiDate';
import { formatApiDateTimeJalali } from '../../utils/dateUtils';
import { useListReturnState } from '../../hooks/useListReturnState';

const { Option } = Select;

const PACKAGE_TYPE_LABEL = {
  monthly: 'ماهانه',
  annual: 'سالانه',
  lifetime: 'مادام‌العمر',
};

const STATUS_LABEL = {
  active: 'فعال',
  inactive: 'غیرفعال',
  license_expired: 'منقضی مجوز',
};

const DEFAULT_RANGE = () => {
  const end = moment();
  const start = moment().subtract(1, 'jYear');
  const fmt = (m) => m.format('jYYYY-jMM-jDD').replace(/\//g, '-');
  return { startDate: fmt(start), endDate: fmt(end) };
};

const CompaniesSettings = () => {
  const navigate = useNavigate();
  const [{ startDate, endDate }, setRange] = useState(DEFAULT_RANGE);
  const [startVal, setStartVal] = useState(null);
  const [endVal, setEndVal] = useState(null);
  const [packageId, setPackageId] = useState('all');
  const [packageType, setPackageType] = useState('all');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [packageOptions, setPackageOptions] = useState([]);

  const captureCurrentListState = useCallback(
    () => ({
      startDate,
      endDate,
      startVal,
      endVal,
      packageId,
      packageType,
      status,
      search,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    }),
    [startDate, endDate, startVal, endVal, packageId, packageType, status, search, pagination.current, pagination.pageSize],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.startDate) setRange((prev) => ({ ...prev, startDate: restored.startDate }));
    if (restored.endDate) setRange((prev) => ({ ...prev, endDate: restored.endDate }));
    if (restored.startVal !== undefined) setStartVal(restored.startVal);
    if (restored.endVal !== undefined) setEndVal(restored.endVal);
    if (restored.packageId) setPackageId(restored.packageId);
    if (restored.packageType) setPackageType(restored.packageType);
    if (restored.status) setStatus(restored.status);
    if (restored.search != null) setSearch(restored.search);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current: restored.pagination.current || 1,
        pageSize: restored.pagination.pageSize || prev.pageSize,
      }));
    }
  });

  const fetchPackages = useCallback(async () => {
    try {
      const res = await packagesApi.getAll({ page: 1, per_page: 500 });
      const list = res?.data?.data || [];
      setPackageOptions(list);
    } catch {
      setPackageOptions([]);
    }
  }, []);

  const fetchList = useCallback(
    async (page = 1, perPage = 10) => {
      setLoading(true);
      try {
        const sd = jalaliValueToApi(startVal) || startDate;
        const ed = jalaliValueToApi(endVal) || endDate;
        const params = {
          startDate: sd,
          endDate: ed,
          package_id: packageId,
          package_type: packageType,
          status,
          search: search.trim() || undefined,
          page,
          per_page: perPage,
        };
        const res = await companiesApi.list(params);
        const data = res?.data || {};
        setRows(data.data || []);
        setPagination({
          current: Number(data.current_page || page),
          pageSize: Number(data.per_page || perPage),
          total: Number(data.total || 0),
        });
        setRange({ startDate: sd, endDate: ed });
      } catch (error) {
        message.error(error?.message || 'خطا در دریافت شرکت‌ها');
      } finally {
        setLoading(false);
      }
    },
    [startDate, endDate, startVal, endVal, packageId, packageType, status, search],
  );

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  useEffect(() => {
    fetchList(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- همان الگوی ایندکس سفارشات؛ صفحه با جدول تنظیم می‌شود
  }, [startVal, endVal, packageId, packageType, status, search]);

  const onDelete = useCallback(
    async (id) => {
      try {
        await companiesApi.delete(id);
        message.success('شرکت حذف شد');
        fetchList(pagination.current, pagination.pageSize);
      } catch (error) {
        message.error(error?.message || 'خطا در حذف');
      }
    },
    [fetchList, pagination.current, pagination.pageSize],
  );

  const columns = useMemo(
    () => [
      {
        title: 'نام شرکت',
        dataIndex: 'company_name',
        key: 'company_name',
        ellipsis: true,
        render: (t, r) => (
          <div className="flex items-center gap-3 min-w-0 py-0.5">
            {r.logo_url ? (
              <span className="inline-flex flex-shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-700">
                <img src={r.logo_url} alt="" className="block h-9 w-9 object-cover sm:h-10 sm:w-10" />
              </span>
            ) : null}
            <span className="min-w-0 flex-1 truncate ps-0.5" style={{ fontFamily: 'CustomFont' }}>
              {t}
            </span>
          </div>
        ),
      },
      {
        title: 'وضعیت',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (s) => (
          <Tag color={s === 'active' ? 'green' : s === 'license_expired' ? 'orange' : 'default'}>
            {STATUS_LABEL[s] || s || '—'}
          </Tag>
        ),
      },
      {
        title: 'بسته',
        key: 'package',
        ellipsis: true,
        render: (_, r) => r.package?.name || '—',
      },
      {
        title: 'نوع بسته',
        dataIndex: 'package_type',
        key: 'package_type',
        width: 100,
        responsive: ['md'],
        render: (t) => PACKAGE_TYPE_LABEL[t] || t || '—',
      },
      {
        title: 'کاربران',
        dataIndex: 'users_count',
        key: 'users_count',
        width: 88,
        responsive: ['lg'],
      },
      {
        title: 'کارمند',
        dataIndex: 'totalEmployees',
        key: 'totalEmployees',
        width: 88,
        responsive: ['lg'],
      },
      {
        title: 'مشتری',
        dataIndex: 'totalClient',
        key: 'totalClient',
        width: 88,
        responsive: ['lg'],
      },
      {
        title: 'انقضای مجوز',
        dataIndex: 'licence_expire_on',
        key: 'licence_expire_on',
        ellipsis: true,
        responsive: ['xl'],
        render: (t) => formatApiDateTimeJalali(t) || '—',
      },
      {
        title: 'عملیات',
        key: 'actions',
        width: 168,
        align: 'center',
        fixed: 'right',
        render: (_, r) => (
          <div className="flex w-full justify-center">
            <Space size="small">
              <Tooltip title="مشاهده">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => navigateToDetail(`/settings/companies/${r.id}`)}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                />
              </Tooltip>
              <Tooltip title="ویرایش">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => navigateToDetail(`/settings/companies/${r.id}/edit`)}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                />
              </Tooltip>
              <Popconfirm title="حذف این شرکت؟" okText="حذف" cancelText="لغو" onConfirm={() => onDelete(r.id)}>
                <Tooltip title="حذف">
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </Space>
          </div>
        ),
      },
    ],
    [navigateToDetail, onDelete],
  );

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0">
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex-shrink-0">
                <BankOutlined className="text-lg sm:text-xl text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 truncate">شرکت‌ها</h1>
                <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm m-0 truncate">مدیریت شرکت‌های ثبت‌شده در سیستم</p>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} loading={loading} onClick={() => fetchList(pagination.current, pagination.pageSize)}>
                بروزرسانی
              </Button>
              <Button type="primary" icon={<PlusOutlined />} className={PANEL_PRIMARY_BTN_CLASS} onClick={() => navigate('/settings/companies/create')}>
                شرکت جدید
              </Button>
            </Space>
          </div>

          <div className="bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-6 xl:grid-cols-8">
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="جستجو در نام شرکت..."
                prefix={<SearchOutlined className="text-gray-400" />}
                className="rounded-lg lg:col-span-2"
                style={{ fontFamily: 'CustomFont', width: '100%' }}
                size="middle"
              />
              <Select
                value={packageId}
                onChange={setPackageId}
                popupClassName="custom-font"
                className="min-w-0 lg:col-span-1"
                style={{ width: '100%', fontFamily: 'CustomFont' }}
              >
                <Option value="all">همه بسته‌ها</Option>
                {packageOptions.map((p) => (
                  <Option key={p.id} value={String(p.id)}>
                    {p.name}
                  </Option>
                ))}
              </Select>
              <Select
                value={packageType}
                onChange={setPackageType}
                popupClassName="custom-font"
                style={{ width: '100%', fontFamily: 'CustomFont' }}
              >
                <Option value="all">همه انواع بسته</Option>
                <Option value="monthly">ماهانه</Option>
                <Option value="annual">سالانه</Option>
                <Option value="lifetime">مادام‌العمر</Option>
              </Select>
              <Select
                value={status}
                onChange={setStatus}
                popupClassName="custom-font"
                style={{ width: '100%', fontFamily: 'CustomFont' }}
              >
                <Option value="all">همه وضعیت‌ها</Option>
                <Option value="active">فعال</Option>
                <Option value="inactive">غیرفعال</Option>
                <Option value="license_expired">منقضی مجوز</Option>
              </Select>
              <div className="grid grid-cols-2 gap-2 lg:col-span-2 xl:col-span-2">
                <PersianDatePicker value={startVal} onChange={setStartVal} placeholder="از تاریخ" />
                <PersianDatePicker value={endVal} onChange={setEndVal} placeholder="تا تاریخ" />
              </div>
            </div>

            <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
              <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={rows}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  responsive: true,
                  onChange: (page, pageSize) => fetchList(page, pageSize),
                }}
                scroll={{ x: 'max-content' }}
              />
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default CompaniesSettings;
